import React, { useState, useEffect, useContext } from 'react';
import {
    ModalCard,
    SimpleCell,
    Spinner,
    Button,
    Spacing
} from '@vkontakte/vkui';
import { Icon24Cancel, Icon36CoinsStacks2Outline } from '@vkontakte/icons';
import { useRouteNavigator } from "@vkontakte/vk-mini-apps-router";
import { getCoinPackages } from '../../services/payment';
import { SnackbarContext } from '../../context/SnackbarContext';
import { useVKPayments } from '../../hooks/useVKPayments';
import BonusStore from '../../store/bonus';
import {
    MODAL_CARD_CLASS,
    MODAL_HEADER_CLASS,
    MODAL_INNER_CARD_CLASS,
    MODAL_INNER_TEXT_CLASS,
    MODAL_PRIMARY_BUTTON_CLASS,
    MODAL_SECONDARY_BUTTON_CLASS,
    MODAL_SUBHEADER_CLASS
} from "../modalStyles";

/**
 * BuyCoinsModal - модальное окно для покупки игровых монет
 * Отображает доступные наборы монет и обрабатывает процесс покупки через VK Bridge
 */
const BuyCoinsModal = ({ nav }) => {
    const routeNavigator = useRouteNavigator();
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { showSnackbar, showErrorSnackbar, showPurchaseSuccessSnackbar } = useContext(SnackbarContext);
    const { isProcessing, isPaymentSupported, initiatePurchase } = useVKPayments();

    // Загрузка наборов монет при открытии модального окна
    useEffect(() => {
        loadCoinPackages();
    }, []);

    const loadCoinPackages = () => {
        setLoading(true);
        setError(null);
        
        getCoinPackages()
            .then((response) => {
                setPackages(response.data.response);
            })
            .catch((err) => {
                console.error('Failed to load coin packages:', err);
                
                let errorMessage = 'Не удалось загрузить наборы монет';
                
                if (err.response) {
                    // Ошибка от сервера
                    if (err.response.status >= 500) {
                        errorMessage = 'Временные проблемы на сервере. Попробуйте позже.';
                    } else if (err.response.status >= 400) {
                        errorMessage = 'Некорректный запрос. Попробуйте позже.';
                    }
                } else if (err.request) {
                    // Проблемы с сетью
                    errorMessage = 'Проблемы с соединением. Проверьте интернет.';
                } else {
                    // Другие ошибки
                    errorMessage = err.message || 'Неизвестная ошибка';
                }
                
                // Сообщение рендерится в модальном окне, поэтому фиксируем именно текст для игрока.
                console.error('Показана ошибка загрузки наборов монет:', errorMessage);
                setError(errorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    };



    const handlePackageSelect = (pkg) => {
        setSelectedPackage(pkg);
    };

    const handlePurchase = () => {
        if (!selectedPackage) {
            showErrorSnackbar('Выберите набор монет');
            return;
        }

        if (!isPaymentSupported()) {
            showErrorSnackbar('Платежи не поддерживаются в данной версии приложения');
            return;
        }

        initiatePurchase(selectedPackage.item_id)
            .then((result) => {
                if (result.success) {
                    // Добавляем монеты в локальный store
                    BonusStore.debit(selectedPackage.coins_amount);
                    showPurchaseSuccessSnackbar(selectedPackage.coins_amount);
                    routeNavigator.hideModal();
                } else {
                    showSnackbar('Статус покупки неопределен. Проверьте баланс через несколько минут.');
                }
            })
            .catch((error) => {
                console.error('Purchase failed:', error);
                
                let errorMessage = 'Ошибка при покупке. Попробуйте позже.';
                
                if (error.response) {
                    // Ошибка от сервера
                    if (error.response.status === 402) {
                        errorMessage = 'Недостаточно голосов для покупки';
                    } else if (error.response.status >= 500) {
                        errorMessage = 'Временные проблемы на сервере. Попробуйте позже.';
                    } else if (error.response.data?.message) {
                        errorMessage = error.response.data.message;
                    }
                } else if (error.request) {
                    // Проблемы с сетью
                    errorMessage = 'Проблемы с соединением. Проверьте интернет.';
                } else {
                    // Другие ошибки
                    errorMessage = error.message || 'Ошибка при покупке. Попробуйте позже.';
                }
                
                showErrorSnackbar(errorMessage);
            });
    };

    if (loading) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Spinner size="large" />}
                header={<span className={MODAL_HEADER_CLASS}>Загрузка...</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>Получаем доступные наборы монет</span>}
            />
        );
    }

    if (error) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Icon24Cancel className="text-brand-danger" />}
                header={<span className={MODAL_HEADER_CLASS}>Ошибка загрузки</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>{error}</span>}
                actions={
                    <div className="flex flex-col w-full gap-2">
                        <Button
                            size="l"
                            mode="primary"
                            className={MODAL_PRIMARY_BUTTON_CLASS}
                            onClick={loadCoinPackages}
                        >
                            Повторить
                        </Button>
                        <Button
                            size="l"
                            mode="secondary"
                            className={MODAL_SECONDARY_BUTTON_CLASS}
                            onClick={() => routeNavigator.hideModal()}
                        >
                            Закрыть
                        </Button>
                    </div>
                }
            />
        );
    }

    if (packages.length === 0) {
        return (
            <ModalCard
                nav={nav}
                onClose={() => routeNavigator.hideModal()}
                className={MODAL_CARD_CLASS}
                icon={<Icon36CoinsStacks2Outline className="text-yellow-500" />}
                header={<span className={MODAL_HEADER_CLASS}>Наборы недоступны</span>}
                subheader={<span className={MODAL_SUBHEADER_CLASS}>Наборы монет временно недоступны. Попробуйте позже.</span>}
                actions={
                    <Button
                        size="l"
                        mode="secondary"
                        className={MODAL_SECONDARY_BUTTON_CLASS}
                        onClick={() => routeNavigator.hideModal()}
                    >
                        Закрыть
                    </Button>
                }
            />
        );
    }

    return (
        <ModalCard
            nav={nav}
            onClose={() => routeNavigator.hideModal()}
            className={MODAL_CARD_CLASS}
            icon={<Icon36CoinsStacks2Outline className="text-yellow-500" />}
            header={<span className={MODAL_HEADER_CLASS}>Купить монеты</span>}
            subheader={<span className={MODAL_SUBHEADER_CLASS}>Выберите набор монет для пополнения баланса</span>}
            actions={
                <div className="flex flex-col w-full gap-2">
                    {!isPaymentSupported() && (
                        <div className="text-sm text-center text-yellow-700 mb-2">
                            ⚠️ Платежи недоступны в данной версии приложения
                        </div>
                    )}

                    <div className={`w-full text-xs p-2 rounded-lg ${MODAL_INNER_CARD_CLASS}`}>
                        <span className={MODAL_INNER_TEXT_CLASS}>
                            Голоса ВКонтакте — универсальная условная единица для приобретения платных возможностей приложений ВКонтакте.{" "}
                            <a href="https://vk.com/faq18514" target="_blank" className={"font-bold text-yellow-700"}>Как приобрести голоса</a>.
                        </span>
                    </div>
                    
                    <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
                        {packages.map((pkg) => (
                            <SimpleCell
                                key={pkg.id}
                                before={<Icon36CoinsStacks2Outline className="text-yellow-500" />}
                                after={<span className="text-gray-800">{`${pkg.price_votes} голосов`}</span>}
                                onClick={() => handlePackageSelect(pkg)}
                                className={`cursor-pointer rounded-lg my-1 transition-all text-gray-800 ${
                                    selectedPackage?.id === pkg.id 
                                        ? 'bg-brand-success/20 border-2 border-brand-success/70 text-gray-900' 
                                        : 'border border-gray-300/70 bg-gray-100 hover:bg-gray-300/70 hover:text-gray-900'
                                }`}
                            >
                                <div>
                                    <div className="font-bold text-gray-900">{pkg.name}</div>
                                    <div className="text-sm text-gray-700">
                                        {pkg.coins_amount} монет
                                    </div>
                                </div>
                            </SimpleCell>
                        ))}
                    </div>
                    
                    <Spacing size={8} />
                    
                    <Button
                        size="l"
                        mode="primary"
                        onClick={handlePurchase}
                        disabled={!selectedPackage || isProcessing || !isPaymentSupported()}
                        loading={isProcessing}
                        className={MODAL_PRIMARY_BUTTON_CLASS}
                    >
                        {!isPaymentSupported() 
                            ? 'Платежи недоступны'
                            : isProcessing 
                                ? 'Обработка платежа...'
                                : selectedPackage 
                                    ? `Купить за ${selectedPackage.price_votes} голосов` 
                                    : 'Выберите набор'
                        }
                    </Button>
                    
                    <Button
                        size="l"
                        mode="secondary"
                        className={MODAL_SECONDARY_BUTTON_CLASS}
                        onClick={() => routeNavigator.hideModal()}
                    >
                        Отмена
                    </Button>
                </div>
            }
        />
    );
};

export default BuyCoinsModal;
